/**
 * TanStack Query hooks for the tasks domain.
 *
 * Read hooks consume the list and single-task endpoints from spec
 * section 6.2; mutation hooks wrap the create / move / complete /
 * patch / delete actions with optimistic updates where the spec
 * tolerates them.
 *
 * Reference: spec sections 6.2, 7.3 (TanStack Query patterns).
 */

import { useAuth } from '@clerk/clerk-react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateTaskInput,
  MoveTaskInput,
  SnoozeTaskInput,
  Subtask,
  SubtaskCreateInput,
  SubtaskUpdateInput,
  Task,
  UpdateTaskInput,
} from '@workspace/shared';

import { apiRequest } from '../api-client';

/** Shared key prefix so cache invalidation lands on every tasks query. */
export const TASKS_KEY = ['tasks'] as const;

interface ListTasksResponse {
  tasks: Task[];
  total: number;
}

interface CompleteTaskResponse {
  task: Task;
  autoPulledTaskId: string | null;
}

interface MoveTaskResponse {
  task: Task;
  affectedColumns: Task[];
}

/**
 * `useAuth` exposes `getToken` which returns a fresh Clerk JWT. Every
 * mutation reads it once at call time; the in-flight token can rotate
 * mid-session so we never cache it.
 */
function useTokenGetter() {
  const { getToken } = useAuth();
  return async (): Promise<string> => {
    const token = await getToken();
    if (!token) throw new Error('No active Clerk session');
    return token;
  };
}

/** Fetch a single task by id. Returns undefined while loading. */
export function useTask(id: string | null): UseQueryResult<Task, Error> {
  const tokenGetter = useTokenGetter();
  return useQuery({
    queryKey: [...TASKS_KEY, 'single', id],
    enabled: id !== null,
    queryFn: async () => {
      const token = await tokenGetter();
      return apiRequest<Task>(`/api/v1/tasks/${id}`, { token });
    },
  });
}

/**
 * Options accepted by the `useTasks` list hook.
 *
 * `includeCompletedSince` (UAT-45) widens the response to include rows
 * whose `completedAt` is on or after the supplied ISO date (YYYY-MM-DD).
 * The Board view passes today's local date so the DONE column shows
 * the current day's completions; surfaces that only want open tasks
 * (the default) leave the option unset.
 */
export interface UseTasksOptions {
  /** ISO date (YYYY-MM-DD). When set, the list also returns tasks
   * completed on or after this date in addition to the open backlog. */
  includeCompletedSince?: string;
}

/**
 * List the open tasks plus, optionally, recent completions.
 *
 * The default call returns every non-completed, non-archived task; the
 * Today and Table sub-views consume this shape directly. Passing
 * `includeCompletedSince` joins in the day's DONE rows for the kanban
 * Board so the DONE column populates with today's wins (and only today's
 * wins; tomorrow's render filters yesterday out via a new `since`).
 */
export function useTasks(options: UseTasksOptions = {}): UseQueryResult<ListTasksResponse, Error> {
  const tokenGetter = useTokenGetter();
  const { includeCompletedSince } = options;
  return useQuery({
    // Keep the since value in the query key so two callers with
    // different since dates don't share a stale cache entry.
    queryKey: [...TASKS_KEY, 'list', { includeCompletedSince: includeCompletedSince ?? null }],
    queryFn: async () => {
      const token = await tokenGetter();
      const params = new URLSearchParams({ limit: '500' });
      if (includeCompletedSince) {
        params.set('includeCompletedSince', includeCompletedSince);
      }
      return apiRequest<ListTasksResponse>(`/api/v1/tasks?${params.toString()}`, { token });
    },
  });
}

/**
 * List completed tasks whose `completedAt` falls on or after the given
 * calendar date.
 *
 * The Round-5 Tasks-views rewrite (UAT-44) added Completed today and
 * Completed this week sections to both the Today landing and the Table
 * view. The backend route accepts a `since` parameter (`YYYY-MM-DD`)
 * which it inflates to a UTC midnight Date and applies as
 * `completedAt: { gte }`; the response comes back ordered by
 * `completedAt desc` so the most-recent completion sits at the top of
 * each section.
 *
 * @param sinceIso Lower bound on `completedAt` as a wire-format
 *   calendar date (`YYYY-MM-DD`). The Today section passes today and
 *   the This-week section passes seven days ago.
 */
export function useCompletedTasksSince(sinceIso: string): UseQueryResult<ListTasksResponse, Error> {
  const tokenGetter = useTokenGetter();
  return useQuery({
    queryKey: [...TASKS_KEY, 'completed', sinceIso],
    queryFn: async () => {
      const token = await tokenGetter();
      // Cap at 200 because a single-user CEO app should never generate
      // more completions than that in a week. The bound is a defensive
      // ceiling rather than a paging mechanism.
      return apiRequest<ListTasksResponse>(
        `/api/v1/tasks?completed=true&since=${encodeURIComponent(sinceIso)}&limit=200`,
        { token },
      );
    },
  });
}

export function useCreateTask(): UseMutationResult<Task, Error, CreateTaskInput> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const token = await tokenGetter();
      return apiRequest<Task>('/api/v1/tasks', { method: 'POST', body: input, token });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}

export function useUpdateTask(): UseMutationResult<
  Task,
  Error,
  { id: string; patch: UpdateTaskInput }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => {
      const token = await tokenGetter();
      return apiRequest<Task>(`/api/v1/tasks/${id}`, { method: 'PATCH', body: patch, token });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}

export function useDeleteTask(): UseMutationResult<{ success: true }, Error, string> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const token = await tokenGetter();
      return apiRequest<{ success: true }>(`/api/v1/tasks/${id}`, { method: 'DELETE', token });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}

export function useCompleteTask(): UseMutationResult<CompleteTaskResponse, Error, string> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const token = await tokenGetter();
      return apiRequest<CompleteTaskResponse>(`/api/v1/tasks/${id}/complete`, {
        method: 'POST',
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}

/**
 * Reverse a completion.
 *
 * Calls `POST /api/v1/tasks/:id/uncomplete` which clears `completedAt`
 * and defaults the task back into DOING (the user can move it elsewhere
 * afterwards). Used by the inline completion checkbox the Round-5
 * Tasks-views rewrite (UAT-44) added to the shared TaskTable so Peter
 * can mark a row open again from the same control he used to close it.
 *
 * Invalidates the entire tasks cache prefix so the open list, the
 * single-task detail (audit log) and the completed-this-week query all
 * refetch in one round-trip.
 */
export function useUncompleteTask(): UseMutationResult<Task, Error, string> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const token = await tokenGetter();
      return apiRequest<Task>(`/api/v1/tasks/${id}/uncomplete`, {
        method: 'POST',
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}

export function useMoveTask(): UseMutationResult<
  MoveTaskResponse,
  Error,
  MoveTaskInput & { id: string }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...rest }) => {
      const token = await tokenGetter();
      return apiRequest<MoveTaskResponse>(`/api/v1/tasks/${id}/move`, {
        method: 'POST',
        body: rest,
        token,
      });
    },
    // Optimistic update: flip the moved task's status in every cached
    // `list` query so the drop animation lands instantly regardless of
    // which `includeCompletedSince` variant the caller used. The query
    // invalidation on success refetches the canonical order.
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: TASKS_KEY });
      // UAT-45 introduced a second cache key variant for the Board
      // (`includeCompletedSince=<today>`) alongside the original
      // open-only list, so snapshot every matching entry and patch each
      // in place rather than touching a single hard-coded key.
      const previous = qc.getQueriesData<ListTasksResponse>({
        queryKey: [...TASKS_KEY, 'list'],
      });
      for (const [key, value] of previous) {
        if (!value) continue;
        qc.setQueryData<ListTasksResponse>(key, {
          ...value,
          tasks: value.tasks.map((t) => (t.id === id ? { ...t, status } : t)),
        });
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) {
        for (const [key, value] of context.previous) {
          qc.setQueryData(key, value);
        }
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}

export function useSnoozeTask(): UseMutationResult<Task, Error, { id: string; until: string }> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, until }) => {
      const token = await tokenGetter();
      const body: SnoozeTaskInput = { until };
      return apiRequest<Task>(`/api/v1/tasks/${id}/snooze`, { method: 'POST', body, token });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}

export function useFrogTask(): UseMutationResult<Task, Error, { id: string; on?: boolean }> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, on = true }) => {
      const token = await tokenGetter();
      const method = on ? 'POST' : 'DELETE';
      return apiRequest<Task>(`/api/v1/tasks/${id}/frog`, { method, token });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}

// ────────────────────────────────────────────────────────────────────
// Task subtasks (UAT-25)
// ────────────────────────────────────────────────────────────────────

/**
 * Create a new subtask under the given task. The backend assigns the
 * next orderIndex and returns the created row.
 */
export function useCreateTaskSubtask(): UseMutationResult<
  Subtask,
  Error,
  { taskId: string; input: SubtaskCreateInput }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, input }) => {
      const token = await tokenGetter();
      return apiRequest<Subtask>(`/api/v1/tasks/${taskId}/subtasks`, {
        method: 'POST',
        body: input,
        token,
      });
    },
    onSuccess: () => {
      // Invalidate the single-task cache so the slide-over re-renders
      // with the new subtask appended.
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}

/**
 * Patch a subtask (title, done, orderIndex). `done=true` triggers the
 * backend's completedAt stamp.
 */
export function useUpdateTaskSubtask(): UseMutationResult<
  Subtask,
  Error,
  { taskId: string; subtaskId: string; patch: SubtaskUpdateInput }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, subtaskId, patch }) => {
      const token = await tokenGetter();
      return apiRequest<Subtask>(`/api/v1/tasks/${taskId}/subtasks/${subtaskId}`, {
        method: 'PATCH',
        body: patch,
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}

/** Hard-delete a subtask. */
export function useDeleteTaskSubtask(): UseMutationResult<
  { success: true },
  Error,
  { taskId: string; subtaskId: string }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, subtaskId }) => {
      const token = await tokenGetter();
      return apiRequest<{ success: true }>(`/api/v1/tasks/${taskId}/subtasks/${subtaskId}`, {
        method: 'DELETE',
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}
